import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, Stack, Typography} from '@mui/material';
import {ClientsService} from '@store/clients/services';

import dayjs from '@utils/dayjs.utils';

import ModalComponent from '@components/modal/Modal.component';
import {CreateBatchExecution, executionsService, ExecutionStatus} from '@store/operations/executions';
import {useSnackbar} from 'notistack';
import {finalize, switchMap} from 'rxjs';
import NewTableComponent from '@components/table/NewTable.component';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {OtherActionType} from '@utils/table/columnTypes/actions.columnType';
import {executionLineService} from '@store/invoices/executionLines';
import {Warning} from '@mui/icons-material';

interface SelectClientServicesModalProps {
  clientServices: { quantity: number; service: ClientsService }[];
  executionDate?: Date;
  handleClose: () => void;
  handleBack: () => void;
}

const SelectClientServicesModal = (props: SelectClientServicesModalProps) => {
  const {clientServices, executionDate, handleClose, handleBack} = props;

  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [executions, setExecutions] = useState<CreateBatchExecution[]>(
    clientServices.reduce((acc: CreateBatchExecution[], cs) => {
      const executions: CreateBatchExecution[] = Array.from({length: cs.quantity}, (_, i) => ({
        id: acc.length + i + 1,
        plannedFrom: dayjs(executionDate).hour(9).minute(0).second(0).millisecond(0).toDate(),
        plannedTo: dayjs(executionDate).hour(17).minute(0).second(0).millisecond(0).toDate(),
        executedAt: executionDate ? dayjs(executionDate).set('hour', 12).set('minute', 0).set('second', 0).toDate() : undefined,
        status: ExecutionStatus.EXECUTED,
        quantity1: 1,
        quantity2: 0,
        service: cs.service,
      }));
      return acc.concat(executions);
    }, [])
  );

  const deleteExecution = (executionId: number) => setExecutions((state) => state.filter((e) => e.id !== executionId));
  const duplicateExecution = (executionId: number) =>
    setExecutions((state) => {
      const execution = state.find((e) => e.id === executionId);
      if (execution) {
        return state.concat({...execution, id: state.length + 1});
      }
      return state;
    });

  const handleValidate = () => {
    setLoading(true);
    executionsService
      .createExecutionsBatch(executions)
      .pipe(finalize(() => setLoading(false)), switchMap(() => executionLineService.getExecutionLines()))
      .subscribe({
        next: () => {
          enqueueSnackbar(t('operations.executions.success.create', {count: executions.length}), {
            variant: 'success',
          });
          handleClose();
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
  };

  const allDatesAreValid = executions.every((e) => e.executedAt != null);

  return (
    <ModalComponent
      aria-label="small-padding"
      handleClose={handleClose}
      maxWidth={false}
      title={t('operations.batchExecutions.fillExecutionsData')}
      content={
        <Stack pt="20px" spacing={2} direction="column" width="100%" height="85vh">
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={handleBack}>
              {t('operations.batchExecutions.back')}
            </Button>
          </Stack>
          <Stack flex={1} width="100%" spacing={2}>
            <NewTableComponent
              // setIsEditing={setIsEditing}
              // disableRowSelection
              editableFields={['executedAt', 'quantity1', 'quantity2']}
              disablePagination
              columns={[
                COLUMN_TYPES.DATE({
                  field: 'executedAt',
                  label: t('global.dateLabel'),
                  width: 200,
                }),
                COLUMN_TYPES.NUMBER({
                  field: 'quantity1',
                  label: t('operations.executions.table.quantity1'),
                  width: 200,
                  endAdornment: (row: CreateBatchExecution) => <Typography fontSize={8}
                                                                           fontWeight="400">
                    {row.service?.execUnitName || row.service?.execUnitName1}
                  </Typography>,
                }),
                COLUMN_TYPES.NUMBER({
                  field: 'quantity2',
                  label: t('operations.executions.table.quantity2'),
                  width: 200,
                  endAdornment: (row: CreateBatchExecution) => <Typography fontSize={8}
                                                                           fontWeight="400">
                    {row.service?.execUnitName2}
                  </Typography>,
                }),
                COLUMN_TYPES.TEXT_BOLD({
                  field: 'service.clientOrganisationUnitName',
                  label: t('operations.executions.table.clientUnitName'),
                  valueGetter: (params: GridValueGetterParams<any, CreateBatchExecution>) =>
                    params.row.service?.clientOrganisationUnitName,
                  fullWidth: true,
                  editable: false,
                  minWidth: 250,
                }),
                COLUMN_TYPES.TEXT_BOLD({
                  field: 'service.clientWasteName',
                  label: t('operations.executions.table.service'),
                  valueGetter: (params: GridValueGetterParams<any, CreateBatchExecution>) =>
                    params.row.service?.clientWasteName,
                  fullWidth: true,
                  editable: false,
                  minWidth: 250,
                }),
                COLUMN_TYPES.TEXT_BOLD({
                  field: 'service.kindName',
                  label: t('operations.executions.table.type'),
                  valueGetter: (params: GridValueGetterParams<any, CreateBatchExecution>) => params.row.service?.kindName,
                  fullWidth: true,
                  editable: false,
                  minWidth: 250,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'service.wasteName',
                  color: 'secondary',
                  label: t('operations.executions.table.waste'),
                  valueGetter: (params: GridValueGetterParams<any, CreateBatchExecution>) =>
                    params.row.service?.wasteName,
                  fullWidth: true,
                  editable: false,
                  minWidth: 250,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'service.containerName',
                  color: 'secondary',
                  label: t('operations.executions.table.container'),
                  valueGetter: (params: GridValueGetterParams<any, CreateBatchExecution>) =>
                    params.row.service?.containerName,
                  fullWidth: true,
                  editable: false,
                  minWidth: 250,
                }),
                COLUMN_TYPES.SELECT({
                  field: 'status',
                  label: t('global.status'),
                  options: ExecutionStatus.selectItems(),
                  editable: false,
                  width: 130,
                }),
                COLUMN_TYPES.ACTIONS({
                  width: 70,
                  editable: false,
                  otherActions: [
                    {
                      type: OtherActionType.EDIT,
                    },
                    {
                      type: OtherActionType.DUPLICATE,
                      handleClick: (execution: CreateBatchExecution) =>
                        execution.id ? duplicateExecution(execution.id) : undefined,
                    },
                    {
                      type: OtherActionType.DELETE,
                      handleClick: (execution: CreateBatchExecution) =>
                        execution.id ? deleteExecution(execution.id) : undefined,
                    },
                  ],
                }),
              ]}
              handleRowChanged={(execution: CreateBatchExecution) => {
                const updatedExecution = {
                  executedAt: dayjs(execution.executedAt).isValid() ? dayjs(execution.executedAt).set('hour', 12).set('minute', 0).set('second', 0).toDate() : undefined,
                  plannedFrom: dayjs(execution.executedAt).isValid() ? dayjs(execution.executedAt).hour(9).minute(0).second(0).millisecond(0).toDate() : execution.plannedFrom,
                  plannedTo: dayjs(execution.executedAt).isValid() ? dayjs(execution.executedAt).hour(9).minute(0).second(0).millisecond(0).toDate() : execution.plannedTo,
                  quantity1: execution.quantity1,
                  quantity2: execution.quantity2,
                  status: execution.status,
                };
                setIsEditing(false);
                setExecutions((state) => state.map((e) => (e.id === execution.id ? {...e, ...updatedExecution} : e)));
                return {...execution, ...updatedExecution};
              }}
              setIsEditing={setIsEditing}
              paginationMode="client"
              data={executions}
            />
          </Stack>
          {!allDatesAreValid && <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}><Warning
              color="error"/><Typography
              color="error">{t('operations.batchExecutions.allDatesMustBeFilled')}</Typography></Stack>}
        </Stack>
      }
      actions={
        <>
          <Button variant="contained" color="primary" disabled={loading || isEditing || !allDatesAreValid}
                  onClick={handleValidate}>
            {t('global.validate')}
          </Button>
        </>
      }
    />
  );
};

export default SelectClientServicesModal;
